import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { OrgWorkerRow as Row } from '@vidmakr/bindings/types';
import { Ban, CheckCircle2, KeyRound, MoreHorizontal, Pencil, Plus, RefreshCw, Trash2, Zap } from 'lucide-react';
import { orgGpuPool } from '@vidmakr/shared';
import { Badge, Button, Card, ConfirmModal, EmptyState, Field, Input, Menu, Modal, PageHeader, cx, type MenuItem } from '@/components/ui';
import { openContextMenu } from '@/components/ContextMenu';
import { WorkerManualUpdate, WorkerSetup, type WorkerToken } from '@/components/WorkerSetup';
import { WorkerUpdateBadge, WorkerUpdateError, isAgentVersion, useLatestWorkerVersion, workerVersionLabel } from '@/components/WorkerUpdate';
import { useOrg } from '@/lib/hooks';
import { reducers } from '@/lib/stdb';
import { attempt, errorMessage, toast } from '@/lib/toast';
import { timeAgo } from '@/lib/format';
import { api } from '@/lib/api';


/** An organization's own GPU workers: machines its members run, serving only this organization's exports. */
export function WorkersPage() {
  const { orgId = '' } = useParams();
  const { org, can } = useOrg(orgId);
  const [rows] = useTable(tables.orgWorkers);
  const [pools] = useTable(tables.workerPools);
  const [jobs] = useTable(tables.job.where((j) => j.orgId.eq(orgId)));
  const workers = useMemo(
    () => rows.filter((w) => w.orgId === orgId).sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name)),
    [rows, orgId],
  );
  const busy = pools.find((p) => p.pool === orgGpuPool(orgId))?.busy ?? 0;
  const latest = useLatestWorkerVersion();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [token, setToken] = useState<WorkerToken | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const [forcing, setForcing] = useState<{ id: string; name: string } | null>(null);

  if (!can('admin')) {
    return (
      <EmptyState icon={<Zap size={32} />} title="GPU workers">
        Only organization admins and owners manage workers.
      </EmptyState>
    );
  }

  const create = async () => {
    setCreating(true);
    try {
      const res = await api<WorkerToken>(`/api/orgs/${orgId}/workers`, { method: 'POST', json: { name } });
      setToken({ ...res, name });
      setAdding(false);
      setName('');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setCreating(false);
    }
  };
  const rotate = async (w: { id: string; name: string }) => {
    try {
      const res = await api<WorkerToken>(`/api/orgs/${orgId}/workers/${w.id}/token`, { method: 'POST' });
      setToken({ ...res, name: w.name });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const items = (w: Row): (MenuItem | 'sep')[] => [
    { label: 'New token / setup command', icon: <KeyRound size={14} />, onClick: () => void rotate(w) },
    { label: 'Rename', icon: <Pencil size={14} />, onClick: () => setRenaming({ id: w.id, name: w.name }) },
    ...(isAgentVersion(w.version)
      ? [
          { label: w.busy ? 'Update and restart (after the current job)' : 'Update and restart', icon: <RefreshCw size={14} />, onClick: () => void attempt(() => reducers().orgRequestWorkerUpdate({ workerId: w.id }), w.busy ? 'The worker updates and restarts once its current job is done' : 'The worker is updating and restarting') },
          ...(w.busy ? [{ label: 'Cancel export and update now', icon: <RefreshCw size={14} />, danger: true, onClick: () => setForcing({ id: w.id, name: w.name }) }] : []),
        ]
      : []),
    w.disabled
      ? { label: 'Enable', icon: <CheckCircle2 size={14} />, onClick: () => void attempt(() => reducers().orgSetWorkerDisabled({ workerId: w.id, disabled: false }), 'Worker enabled') }
      : { label: 'Disable', icon: <Ban size={14} />, onClick: () => void attempt(() => reducers().orgSetWorkerDisabled({ workerId: w.id, disabled: true }), 'Worker disabled') },
    'sep',
    { label: 'Delete', icon: <Trash2 size={14} />, danger: true, onClick: () => setDeleting({ id: w.id, name: w.name }) },
  ];

  return (
    <>
      <PageHeader
        title="GPU workers"
        subtitle={org ? `Machines with an NVIDIA GPU that render ${org.name}'s exports` : undefined}
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus size={16} /> Add worker
          </Button>
        }
      />
      <p className="mb-5 max-w-3xl text-sm text-muted">
        Run a worker on any computer with an NVIDIA graphics card, such as an editing PC. It renders this organization’s exports when
        “Our GPU workers” is picked in the export dialog, using NVENC for fast H.264/H.265 encoding. Workers only connect outwards and only ever
        receive this organization’s jobs.
      </p>
      {workers.length === 0 ? (
        <EmptyState
          icon={<Zap size={32} />}
          title="No GPU workers yet"
          action={
            <Button variant="primary" onClick={() => setAdding(true)}>
              <Plus size={16} /> Add worker
            </Button>
          }
        >
          Add one, then run the command it gives you on the GPU machine.
        </EmptyState>
      ) : (
        <Card className="divide-y divide-line">
          {workers.map((w) => (
            <div key={w.id} className="flex items-center gap-3 px-4 py-3" onContextMenu={(e) => openContextMenu(e, items(w))}>
              <span
                className={cx('h-2.5 w-2.5 shrink-0 rounded-full', w.disabled ? 'bg-faint' : w.online ? 'bg-ok' : 'bg-danger')}
                title={w.disabled ? 'Disabled' : w.online ? 'Online' : 'Offline'}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {w.name}
                  {w.hwaccel === 'nvenc' ? <Badge tone="accent">NVENC</Badge> : w.hwaccel ? <Badge tone="warn">CPU encoding</Badge> : null}
                  {w.disabled ? <Badge tone="danger">disabled</Badge> : null}
                  {w.busy ? <Badge tone="ok">rendering</Badge> : null}
                  <WorkerUpdateBadge w={w} latest={latest} />
                </div>
                <div className="text-xs text-faint">
                  {!w.hostname ? 'never connected · ' : w.hostname !== w.name ? `${w.hostname} · ` : ''}
                  {w.version ? `${workerVersionLabel(w.version)} · ` : ''}
                  {w.online ? 'online' : `last seen ${timeAgo(w.lastSeenAt)}`}
                </div>
                <WorkerUpdateError error={w.updateError} />
              </div>
              <Menu
                trigger={
                  <Button variant="ghost" size="icon-sm" aria-label="Worker actions">
                    <MoreHorizontal size={15} />
                  </Button>
                }
                items={items(w)}
              />
            </div>
          ))}
        </Card>
      )}
      {workers.length ? (
        <div className="mt-3 text-xs text-faint">
          {workers.filter((w) => w.online && !w.disabled).length} of {workers.length} online · {busy} rendering
        </div>
      ) : null}

      <WorkerManualUpdate />

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add GPU worker"
        footer={
          <Button variant="primary" disabled={!name.trim()} loading={creating} onClick={() => void create()}>
            Create worker
          </Button>
        }
      >
        <Field label="Name" hint="Something that identifies the machine">
          <Input
            value={name}
            autoFocus
            placeholder="Editing PC (RTX 4090)"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && name.trim() && void create()}
          />
        </Field>
      </Modal>

      <Modal open={!!token} onClose={() => setToken(null)} title={`Run worker · ${token?.name}`} width={720}>
        {token ? <WorkerSetup t={token} /> : null}
      </Modal>

      <Modal
        open={!!renaming}
        onClose={() => setRenaming(null)}
        title="Rename worker"
        footer={
          <Button
            variant="primary"
            disabled={!renaming?.name.trim()}
            onClick={async () => {
              if (renaming && (await attempt(() => reducers().orgRenameWorker({ workerId: renaming.id, name: renaming.name })))) setRenaming(null);
            }}
          >
            Save
          </Button>
        }
      >
        <Input autoFocus value={renaming?.name ?? ''} onChange={(e) => setRenaming((r) => (r ? { ...r, name: e.target.value } : r))} />
      </Modal>

      <ConfirmModal
        open={!!forcing}
        onClose={() => setForcing(null)}
        title="Cancel export and update now"
        danger
        confirmLabel="Cancel and update"
        message={`"${forcing?.name}" stops what it is rendering (the export is canceled and can be retried), then updates and restarts right away; it is back within a few seconds.`}
        onConfirm={async () => {
          const f = forcing!;
          // A canceled job is dropped by the worker; the update then goes ahead without waiting for it.
          for (const j of jobs) if (j.workerId === f.id && j.status === 'running') await attempt(() => reducers().cancelJob({ jobId: j.id }));
          await attempt(() => reducers().orgRequestWorkerUpdate({ workerId: f.id }), 'Updating and restarting');
        }}
      />

      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete worker"
        danger
        confirmLabel="Delete worker"
        message={`"${deleting?.name}" stops receiving jobs and its token stops working. A running export is put back in the queue.`}
        onConfirm={() => attempt(() => reducers().orgDeleteWorker({ workerId: deleting!.id }), 'Worker deleted')}
      />
    </>
  );
}
